import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from '../../i18n/I18nProvider';

// Fix for default marker icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function ProviderMap({ providers = [], height = '400px', zoom = 8 }) {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const [isMounted, setIsMounted] = useState(false);
  const [mapError, setMapError] = useState(null);

  // Filter providers with valid coordinates
  const providersWithCoords = (providers || []).filter(
    p => p && p.latitude && p.longitude && 
    !isNaN(parseFloat(p.latitude)) && 
    !isNaN(parseFloat(p.longitude))
  );

  // Calculate center of map based on provider locations
  const getCenter = () => {
    if (providersWithCoords.length === 0) {
      // Default to Tunisia center if no providers
      return [33.8869, 9.5375];
    }
    
    if (providersWithCoords.length === 1) {
      return [parseFloat(providersWithCoords[0].latitude), parseFloat(providersWithCoords[0].longitude)];
    }

    // Calculate average center for multiple providers
    const avgLat = providersWithCoords.reduce((sum, p) => sum + parseFloat(p.latitude), 0) / providersWithCoords.length;
    const avgLng = providersWithCoords.reduce((sum, p) => sum + parseFloat(p.longitude), 0) / providersWithCoords.length;
    return [avgLat, avgLng];
  };

  const center = getCenter();

  // Adjust zoom based on number of providers
  const getZoomLevel = () => {
    if (providersWithCoords.length === 0) return 6;
    if (providersWithCoords.length === 1) return 12;
    if (providersWithCoords.length <= 3) return 10;
    return zoom;
  };

  // Wait for component to mount before rendering map
  useEffect(() => {
    // Use setTimeout to ensure DOM is fully ready
    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Handle map errors
  useEffect(() => {
    if (mapError) {
      console.error('Map error:', mapError);
    }
  }, [mapError]);

  // If there's an error, show fallback
  if (mapError) {
    return (
      <div 
        style={{ 
          height, 
          width: '100%', 
          borderRadius: '8px', 
          overflow: 'hidden',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          background: '#f5f5f5',
          color: '#666'
        }}
      >
        <div className="text-center">
            <p className="mb-0">{t('common.mapError')}</p>
            <small>{t('common.mapRefresh')}</small>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height, width: '100%', borderRadius: '8px', overflow: 'hidden' }}>
      {isMounted && providersWithCoords.length > 0 ? (
        <MapContainer
          center={center}
          zoom={getZoomLevel()}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          ref={mapRef}
          key={`map-${providersWithCoords.length}`} // Force re-render when providers change
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {providersWithCoords.map((provider, idx) => (
            <Marker
              key={provider._id || idx}
              position={[parseFloat(provider.latitude), parseFloat(provider.longitude)]}
            >
              <Popup>
                <div>
                  <strong>{provider.name} {provider.lastName}</strong>
                  {provider.businessName && <div><small>{provider.businessName}</small></div>}
                  {provider.city && provider.region && (
                    <div><small>{provider.city}, {provider.region}</small></div>
                  )}
                  {provider.phone && <div><small><em className="icon ni ni-call me-1" style={{ fontStyle: 'normal' }}></em>{provider.phone}</small></div>}
                  {provider.email && (
                    <div>
                      <small>
                        <a href={`mailto:${provider.email}`}>{provider.email}</a>
                      </small>
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      ) : !isMounted ? (
        <div 
          style={{ 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            background: '#f5f5f5',
            color: '#666'
          }}
        >
          <div className="text-center">
            <p className="mb-0">{t('common.mapLoading')}</p>
          </div>
        </div>
      ) : (
        <div 
          style={{ 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            background: '#f5f5f5',
            color: '#666'
          }}
        >
          <div className="text-center">
            <p className="mb-0">{t('common.noLocationData')}</p>
            <small>{t('common.providersMustSetLocation')}</small>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProviderMap;
